import { Directive, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit, Optional, Renderer2 } from '@angular/core';
import { MessageService, SessionService } from '@shared/core';
import { Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

const STYLE_ID = 'spaq-copy-to-clipboard-styles';
const HOST_CLASS = 'spaq-copy-to-clipboard';
const ADMIN_CLASS = 'spaq-copy-to-clipboard--admin';
const COPYABLE_CLASS = 'spaq-copy-to-clipboard--copyable';
const OVERLAY_CLASS = 'spaq-copy-to-clipboard--overlay';
const ICON_CLASS = 'spaq-copy-to-clipboard__icon';
const VOID_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT', 'IMG', 'BR', 'HR', 'COL']);

@Directive({
	selector: '[spaqCopyToClipboard]',
	standalone: true
})
export class CopyToClipboardDirective implements OnInit, OnChanges, OnDestroy {
	@Input('spaqCopyToClipboard') value: string | number | null | undefined;

	private icon: HTMLElement | null = null;
	private isAdmin = false;
	private readonly destroy$ = new Subject<void>();

	constructor(
		private host: ElementRef<HTMLElement>,
		private renderer: Renderer2,
		@Optional() private sessionService: SessionService,
		@Optional() private messageService: MessageService
	) {}

	ngOnInit(): void {
		ensureStyles(this.host.nativeElement.ownerDocument);
		this.renderer.addClass(this.host.nativeElement, HOST_CLASS);

		if (!this.sessionService) {
			return;
		}

		this.sessionService.auth
			.pipe(
				map(session => !!session?.claims?.admin),
				distinctUntilChanged(),
				takeUntil(this.destroy$)
			)
			.subscribe(isAdmin => {
				this.isAdmin = isAdmin;
				this.syncChrome();
			});
	}

	ngOnChanges(): void {
		this.syncChrome();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.removeIcon();
		this.renderer.removeClass(this.host.nativeElement, HOST_CLASS);
		this.renderer.removeClass(this.host.nativeElement, ADMIN_CLASS);
		this.renderer.removeClass(this.host.nativeElement, COPYABLE_CLASS);
		this.renderer.removeClass(this.host.nativeElement, OVERLAY_CLASS);
	}

	@HostListener('click')
	async onClick(): Promise<void> {
		if (!this.isAdmin) {
			return;
		}

		const text = this.resolveText();
		if (!text) {
			return;
		}

		try {
			await navigator.clipboard.writeText(text);
			this.messageService?.show('Copié dans le presse-papiers');
		} catch {
			this.messageService?.show('Impossible de copier', 'error');
		}
	}

	@HostListener('mouseenter')
	onMouseEnter(): void {
		this.syncChrome();
	}

	private syncChrome(): void {
		const el = this.host.nativeElement;
		const copyable = this.isAdmin && !!this.resolveText();

		if (this.isAdmin) {
			this.renderer.addClass(el, ADMIN_CLASS);
			this.ensureIcon();
		} else {
			this.renderer.removeClass(el, ADMIN_CLASS);
			this.removeIcon();
		}

		if (copyable) {
			this.renderer.addClass(el, COPYABLE_CLASS);
		} else {
			this.renderer.removeClass(el, COPYABLE_CLASS);
		}
	}

	private ensureIcon(): void {
		if (this.icon) {
			return;
		}

		const icon = this.renderer.createElement('span');
		this.renderer.addClass(icon, 'k-icon');
		this.renderer.addClass(icon, 'k-i-copy');
		this.renderer.addClass(icon, ICON_CLASS);
		this.renderer.setAttribute(icon, 'aria-hidden', 'true');

		const hostEl = this.host.nativeElement;
		if (VOID_TAGS.has(hostEl.tagName)) {
			const parent = hostEl.parentNode;
			if (parent) {
				this.renderer.insertBefore(parent, icon, hostEl.nextSibling);
			}
		} else {
			const display = getComputedStyle(hostEl).display;
			if (display === 'flex' || display === 'inline-flex') {
				this.renderer.addClass(hostEl, OVERLAY_CLASS);
				this.renderer.setStyle(hostEl, 'position', 'relative');
				this.renderer.setStyle(icon, 'position', 'absolute');
				this.renderer.setStyle(icon, 'top', '50%');
				this.renderer.setStyle(icon, 'right', '12px');
				this.renderer.setStyle(icon, 'left', 'auto');
				this.renderer.setStyle(icon, 'width', '1em');
				this.renderer.setStyle(icon, 'margin', '0');
				this.renderer.setStyle(icon, 'transform', 'translateY(-50%)');
			}
			this.renderer.appendChild(hostEl, icon);
		}

		this.icon = icon;
	}

	private removeIcon(): void {
		if (!this.icon) {
			return;
		}

		const parent = this.icon.parentNode;
		if (parent) {
			this.renderer.removeChild(parent, this.icon);
		}
		this.icon = null;
	}

	private resolveText(): string {
		if (this.value != null && String(this.value) !== '') {
			return String(this.value);
		}

		const hostEl = this.host.nativeElement;
		if (VOID_TAGS.has(hostEl.tagName)) {
			return cleanText((hostEl as HTMLInputElement).value ?? '');
		}

		const children = Array.from(hostEl.children).filter(child => child !== this.icon);
		if (children.length > 1) {
			return children
				.map(child => cleanText(child.textContent ?? ''))
				.filter(text => text.length > 0)
				.join(', ');
		}

		return cleanText(
			Array.from(hostEl.childNodes)
				.filter(node => node !== this.icon)
				.map(node => node.textContent ?? '')
				.join('')
		);
	}
}

function cleanText(text: string): string {
	return text.replace(/\s+/g, ' ').trim();
}

function ensureStyles(doc: Document | null): void {
	if (!doc) {
		return;
	}

	const css = `
		.${HOST_CLASS}.${ADMIN_CLASS} {
			cursor: pointer;
		}
		.${HOST_CLASS} .${ICON_CLASS},
		.${HOST_CLASS} + .${ICON_CLASS} {
			margin-left: 0.35em;
			opacity: 0;
			pointer-events: none;
			vertical-align: middle;
			font-size: 0.9em;
			transition: opacity 0.15s ease;
		}
		.${HOST_CLASS}.${ADMIN_CLASS}.${COPYABLE_CLASS}:hover .${ICON_CLASS},
		.${HOST_CLASS}.${ADMIN_CLASS}.${COPYABLE_CLASS}:hover + .${ICON_CLASS} {
			opacity: 1;
		}
		.${HOST_CLASS}.${OVERLAY_CLASS} {
			position: relative;
		}
		.${HOST_CLASS}.${OVERLAY_CLASS} .${ICON_CLASS} {
			position: absolute;
			top: 50%;
			right: 12px;
			left: auto;
			width: 1em;
			margin: 0;
			transform: translateY(-50%);
		}
	`;

	let style = doc.getElementById(STYLE_ID);
	if (!style) {
		style = doc.createElement('style');
		style.id = STYLE_ID;
		doc.head.appendChild(style);
	}
	style.textContent = css;
}
